import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95 transform-gpu will-change-transform relative overflow-hidden group",
    {
      variants: {
        variant: {
          default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-button hover:shadow-glow hover:scale-[1.02]",
          destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-lg hover:scale-[1.02]",
          outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] hover:border-primary/50",
          secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-[1.02]",
          ghost: "hover:bg-accent hover:text-accent-foreground hover:scale-[1.02]",
          link: "text-primary underline-offset-4 hover:underline hover:scale-[1.02]",
          hero: "bg-gradient-hero text-white hover:shadow-glow hover:scale-105 font-semibold shadow-lg",
          accent: "bg-accent-red text-white hover:bg-accent-red-dark shadow-button hover:shadow-power hover:scale-[1.02]",
          modern: "bg-neutral-dark text-white border border-neutral-dark hover:bg-accent-red hover:border-accent-red shadow-button hover:shadow-glow hover:scale-[1.02]",
          power: "bg-gradient-power text-white hover:shadow-power hover:scale-105 font-semibold shadow-button",
          success: "bg-gradient-success text-white hover:shadow-success hover:scale-105 font-semibold shadow-button",
          performance: "bg-gradient-performance text-white hover:shadow-performance hover:scale-105 font-bold shadow-lg",
          endurance: "bg-gradient-endurance text-white hover:shadow-glow hover:scale-105 font-semibold shadow-button",
        },
        size: {
          default: "h-10 px-4 py-2",
          sm: "h-9 rounded-lg px-3",
          lg: "h-12 rounded-xl px-8 text-base",
          icon: "h-10 w-10",
        },
      },
      defaultVariants: {
        variant: "default",
        size: "default",
      },
    }
  )

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // When using asChild, we can't add additional elements as Slot expects exactly one child
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      )
    }
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {/* Hero variant glow effect */}
        {(variant === "hero" || variant === "power" || variant === "performance") && (
          <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-white/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-out" />
        )}
        
        <span className="relative z-10">{children}</span>
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
